import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
	Autocomplete,
	Button,
	ButtonGroup,
	Grid,
	TextField,
	useMediaQuery,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { renderCodeOption } from "../../utils/functions";
import PrintsSearch3 from "../../components/PrintSearch3";
import PATClearButton from "../../components/Buttons/PATClearButton";

const headers = [
	{ key: "unitNumber", label: "Unit#" },
	{ key: "year", label: "Year" },
	{ key: "make", label: "Make" },
	{ key: "model", label: "Model" },
	{ key: "vin", label: "VIN" },
	{ key: "status", label: "Status" },
	{ key: "odometer", label: "Odometer" },
	{ key: "personnel", label: "Assigned To" },
	{ key: "division", label: "Division" },
	{ key: "service", label: "Service" },
];

const columnsForPrint = headers.map((h) => ({
	field: h.key,
	headerName: h.label,
	editable: false,
	flex: 1,
	...h,
}));

const statusOptions = [
	{
		label: "All Status",
		value: "all",
	},
	{
		label: "In Service",
		value: "in-service",
	},
	{
		label: "Out of service",
		value: "out-service",
	},
	{
		label: "Maintenance",
		value: "maintenance",
	},
	{
		label: "Retired",
		value: "retired",
	},
];

const serviceOptions = [
	{
		label: "Overdue",
		value: "overdue",
	},
	{
		label: "Due Soon",
		value: "due-soon",
	},
	{
		label: "OK",
		value: "ok",
	},
];

const useStyles = makeStyles((theme) => ({
	sideMenuTitle: {
		fontSize: 20,
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: "5px",
	},
	colorTitle: {
		color:
			theme.palette.mode === "dark"
				? "#fff !important"
				: "#000 !important",
	},
}));

const VehiclesTableToolbar = ({
	vehicles,
	search,
	setSearch,
	filters,
	resetFilters,
	onFilterChange,
	onAddClick,
	makeOptions,
	modelOptions,
	yearOptions,
	divisionOptions,
	employeeOptions,
	fundingOptions,
	vehicleSvcStatus,
}) => {
	const theme = useTheme();
	const btnStyles = {
		color: theme.palette.mode === "dark" ? "#fff" : "primary",
		borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
	};
	const classes = useStyles();
	const [printFlag, setPrintFlag] = useState(false);
	const [showAdvSearch, setShowAdvSearch] = useState(false);
	const isSmall = useMediaQuery(theme.breakpoints.down("md"));
	const [localFilters, setLocalFilters] = useState(filters);
	const [localSearch, setLocalSearch] = useState(search || "");

	const printVehicles = useMemo(() => {
		return vehicles.map((v) => {
			const rowStatus = vehicleSvcStatus[v.id];
			const worst = rowStatus?.worst;

			let service = "—";

			if (worst) {
				service = worst.status;
			} else if (v?.serviceRequirements?.length > 0) {
				service = "OK";
			}

			return {
				...v,
				yearMakeModel: `${v?.year || ""} ${v?.make || ""} ${v?.model || ""}`,
				personnel: `${v?.["personnelInfo.FirstName"] || ""} ${
					v?.["personnelInfo.LastName"] || ""
				}`.trim(),
				service,
				odometerText: v?.odometer ? `${v.odometer} mi` : "",
			};
		});
	}, [vehicles, vehicleSvcStatus]);

	useEffect(() => {
		setLocalFilters(filters);
	}, [filters]);

	useEffect(() => {
		setLocalSearch(search || "");
	}, [search]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearchChange = useCallback(
		debounce((searchString) => {
			setSearch(searchString);
		}, 500),
		[setSearch],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFilterChange = useCallback(
		debounce((newFilters) => {
			onFilterChange(newFilters);
		}, 500),
		[onFilterChange],
	);

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setLocalSearch(value);
		debouncedSearchChange(value);
	};

	const handleFilterChange = useCallback(
		(field, value) => {
			const updatedFilters = { ...localFilters, [field]: value };
			setLocalFilters(updatedFilters);
			debouncedFilterChange(updatedFilters);
		},
		[debouncedFilterChange, localFilters],
	);

	const renderClearButton = () => {
		return (
			<>
				<Grid item>
					<Grid item>
						<PATClearButton
							title="Clear Filters"
							onClick={resetFilters}
						/>
					</Grid>
				</Grid>
			</>
		);
	};

	const handlePrintClick = () => {
		setPrintFlag(true);
		setTimeout(() => setPrintFlag(false), 1000);
	};

	const renderFilterFields = (fields) =>
		fields.map(
			({ type, label, field, options, xs = 2, multiselect = true }) => (
				<Grid item xs={isSmall ? 12 : xs} key={field}>
					{type === "text" ? (
						<TextField
							label={label}
							value={localFilters[field] || ""}
							onChange={(e) =>
								handleFilterChange(field, e.target.value)
							}
							fullWidth
							variant="outlined"
							size="small"
						/>
					) : (
						<Autocomplete
							multiple={multiselect}
							options={options || []}
							value={
								multiselect
									? localFilters[field] || []
									: localFilters[field] || null
							}
							onChange={(_, value) =>
								handleFilterChange(field, value)
							}
							getOptionLabel={(option) => option?.label || ""}
							renderOption={renderCodeOption}
							isOptionEqualToValue={(o, v) =>
								o?.value === v?.value
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									variant="outlined"
									size="small"
								/>
							)}
							fullWidth
							sx={
								multiselect
									? {
											"& .MuiAutocomplete-inputRoot": {
												flexWrap: "nowrap",
												maxHeight: "40px",
											},
											"& .MuiAutocomplete-tag": {
												maxWidth: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											},
											"& .MuiAutocomplete-endAdornment": {
												alignSelf: "flex-start",
											},
										}
									: {}
							}
						/>
					)}
				</Grid>
			),
		);

	const filterFields = [
		{
			type: "autocomplete",
			label: "Status",
			field: "status",
			options: statusOptions,
			xs: 1,
			multiselect: true,
		},
		{
			type: "autocomplete",
			label: "Service",
			field: "service",
			options: serviceOptions,
			xs: 1,
			multiselect: true,
		},
	];

	const advanceFilterFields = [
		{
			type: "autocomplete",
			label: "Make",
			field: "make",
			options: makeOptions,
			multiselect: true,
			xs: 1.7,
		},
		{
			type: "autocomplete",
			label: "Model",
			field: "model",
			options: modelOptions,
			multiselect: true,
			xs: 1.7,
		},
		{
			type: "autocomplete",
			label: "Year",
			field: "year",
			options: yearOptions,
			multiselect: true,
			xs: 1.7,
		},
		{
			type: "autocomplete",
			label: "Division",
			field: "division",
			options: divisionOptions,
			multiselect: true,
			xs: 1.5,
		},
		{
			type: "autocomplete",
			label: "Employee",
			field: "perPersonnelId",
			options: employeeOptions,
			multiselect: true,
			xs: 1.5,
		},
		{
			type: "autocomplete",
			label: "Funding",
			field: "fundingSourceId",
			options: fundingOptions,
			multiselect: true,
			xs: 1.5,
		},
	];

	const buttons = [
		<Button
			key="all"
			variant={filters.type === "all" ? "contained" : "outlined"}
			sx={btnStyles}
			onClick={(e) => handleFilterChange("type", "all")}
		>
			All
		</Button>,
		<Button
			key="out"
			variant={filters.type === "out" ? "contained" : "outlined"}
			sx={btnStyles}
			onClick={(e) => {
				handleFilterChange("type", "out");
			}}
		>
			↑
		</Button>,
		<Button
			key="in"
			variant={filters.type === "in" ? "contained" : "outlined"}
			sx={btnStyles}
			onClick={(e) => handleFilterChange("type", "in")}
		>
			↓
		</Button>,
	];

	return (
		<>
			<div>
				<div className={classes.sideMenuTitle}>Fleet Vehicles</div>
			</div>
			<div className="section-actions mb-2">
				<TextField
					label="Search unit, VIN, make, employee, division, license..."
					type="search"
					className={classes.filter}
					variant="outlined"
					size="small"
					style={{
						width: "100%",
						maxWidth: 250,
					}}
					value={localSearch}
					onChange={handleSearchChange}
				/>
				{renderFilterFields([...filterFields])}
				<ButtonGroup size="large" aria-label="Large button group">
					{buttons}
				</ButtonGroup>
				<Button
					variant="outlined"
					color="primary"
					sx={btnStyles}
					onClick={() => setShowAdvSearch((s) => !s)}
				>
					🔍 Advanced
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={handlePrintClick}
					title="Print current list"
					sx={btnStyles}
				>
					🖨️ Print
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={onAddClick}
				>
					+ Add Vehicle
				</Button>
			</div>
			{showAdvSearch && (
				<div
					style={{
						margin: "8px",
						padding: "14px 16px",

						border: "1px solid var(--border)",
						borderRadius: "8px",
						display: "flex",
						flexWrap: "wrap",
						gap: 8,
						alignItems: "center",
					}}
				>
					<span
						style={{
							fontSize: 11,
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "var(--text2)",
							marginRight: 4,
						}}
					>
						Filter by:
					</span>
					{renderFilterFields([...advanceFilterFields])}
					{renderClearButton()}
				</div>
			)}

			<PrintsSearch3
				title="Vehicles"
				data={printVehicles}
				cols={columnsForPrint}
				print={printFlag}
			/>
		</>
	);
};

export default VehiclesTableToolbar;
